import { componentQrl, inlinedQrl, _jsxBranch, useStore, useComputedQrl, useLexicalScope, useVisibleTaskQrl, useTaskQrl, _jsxC, _IMMUTABLE, _fnSignal, _wrapProp } from "@builder.io/qwik";
import { Fragment } from "@builder.io/qwik/jsx-runtime";
import { TARGET } from "../../constants/target.qwik.mjs";
import { getBlockComponentOptions } from "../../functions/get-block-component-options.qwik.mjs";
import { getProcessedBlock } from "../../functions/get-processed-block.qwik.mjs";
import { bindAnimations } from "./animator.qwik.mjs";
import { getRepeatItemData, getComponent, provideBuilderBlock, provideBuilderContext, provideLinkComponent, provideRegisteredComponents, generateKey } from "./block.helpers.qwik.mjs";
import { BlockStyles } from "./components/block-styles.qwik.mjs";
import { BlockWrapper } from "./components/block-wrapper.qwik.mjs";
import { ComponentRef } from "./components/component-ref/component-ref.qwik.mjs";
import { RepeatedBlock } from "./components/repeated-block.qwik.mjs";
const Block = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  var _a;
  _jsxBranch();
  useStore({
    _processedBlock: {
      value: null,
      update: false
    }
  });
  const repeatItem = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return getRepeatItemData({
      block: props2.block,
      context: props2.context
    });
  }, "Block_component_repeatItem_useComputed_j31Y3wFqSCM", [
    props
  ]));
  const processedBlock = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2;
    const [props2] = useLexicalScope();
    const blockToUse = ((_a2 = props2.block.repeat) == null ? void 0 : _a2.collection) ? props2.block : getProcessedBlock({
      block: props2.block,
      localState: props2.context.localState,
      rootState: props2.context.rootState,
      rootSetState: props2.context.rootSetState,
      context: props2.context.context
    });
    return blockToUse;
  }, "Block_component_processedBlock_useComputed_ESKw0l5FcBc", [
    props
  ]));
  const blockComponent = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [processedBlock2, props2] = useLexicalScope();
    return getComponent({
      block: processedBlock2.value,
      registeredComponents: props2.registeredComponents,
      model: props2.context.model
    });
  }, "Block_component_blockComponent_useComputed_83sGy9Xlsi0", [
    processedBlock,
    props
  ]));
  const Tag = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2;
    const [processedBlock2, props2] = useLexicalScope();
    const shouldUseLink = props2.block.tagName === "a" || ((_a2 = processedBlock2.value.properties) == null ? void 0 : _a2.href) || processedBlock2.value.href;
    if (shouldUseLink)
      return props2.linkComponent || "a";
    return props2.block.tagName || "div";
  }, "Block_component_Tag_useComputed_eQnDgbcBW2A", [
    processedBlock,
    props
  ]));
  const canShowBlock = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2, _b;
    const [processedBlock2, props2, repeatItem2] = useLexicalScope();
    if ((_a2 = props2.block.repeat) == null ? void 0 : _a2.collection) {
      if ((_b = repeatItem2.value) == null ? void 0 : _b.length)
        return true;
      return false;
    }
    const shouldHide = "hide" in processedBlock2.value ? processedBlock2.value.hide : false;
    const shouldShow = "show" in processedBlock2.value ? processedBlock2.value.show : true;
    return shouldShow && !shouldHide;
  }, "Block_component_canShowBlock_useComputed_NJEFz8ICF08", [
    processedBlock,
    props,
    repeatItem
  ]));
  const childrenWithoutParentComponent = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2;
    const [blockComponent2, processedBlock2, repeatItem2] = useLexicalScope();
    const shouldRenderChildrenOutsideRef = !((_a2 = blockComponent2.value) == null ? void 0 : _a2.component) && !repeatItem2.value;
    return shouldRenderChildrenOutsideRef ? processedBlock2.value.children ?? [] : [];
  }, "Block_component_childrenWithoutParentComponent_useComputed_0WENYGElWnc", [
    blockComponent,
    processedBlock,
    repeatItem
  ]));
  const componentRefProps = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    var _a2, _b, _c;
    const [blockComponent2, processedBlock2, props2] = useLexicalScope();
    return {
      blockChildren: processedBlock2.value.children ?? [],
      componentRef: (_a2 = blockComponent2.value) == null ? void 0 : _a2.component,
      componentOptions: {
        ...getBlockComponentOptions(processedBlock2.value, props2.context),
        ...provideBuilderBlock(blockComponent2.value, processedBlock2.value),
        ...provideBuilderContext(blockComponent2.value, props2.context),
        ...provideLinkComponent(blockComponent2.value, props2.linkComponent),
        ...provideRegisteredComponents(blockComponent2.value, props2.registeredComponents, props2.context.model)
      },
      context: props2.context,
      linkComponent: props2.linkComponent,
      registeredComponents: props2.registeredComponents,
      builderBlock: processedBlock2.value,
      includeBlockProps: ((_b = blockComponent2.value) == null ? void 0 : _b.noWrap) === true,
      isInteractive: !(((_c = blockComponent2.value) == null ? void 0 : _c.isRSC) && TARGET === "rsc")
    };
  }, "Block_component_componentRefProps_useComputed_Ikbl8VO04ho", [
    blockComponent,
    processedBlock,
    props
  ]));
  useVisibleTaskQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [processedBlock2] = useLexicalScope();
    const blockId = processedBlock2.value.id;
    const animations = processedBlock2.value.animations;
    if (animations && blockId)
      bindAnimations(animations.map((animation) => ({
        ...animation,
        elementId: blockId
      })));
  }, "Block_component_useVisibleTask_hrTYvqw0Tbs", [
    processedBlock
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
  }, "Block_component_useTask_Xb7FQvPjBMo"));
  return /* @__PURE__ */ _jsxC(Fragment, {
    children: canShowBlock.value ? /* @__PURE__ */ _jsxC(Fragment, {
      children: [
        /* @__PURE__ */ _jsxC(BlockStyles, {
          get block() {
            return processedBlock.value;
          },
          get context() {
            return props.context;
          },
          [_IMMUTABLE]: {
            block: _fnSignal((p0) => p0.value, [
              processedBlock
            ], "p0.value"),
            context: _fnSignal((p0) => p0.context, [
              props
            ], "p0.context")
          }
        }, 3, "jN_0"),
        !((_a = blockComponent.value) == null ? void 0 : _a.noWrap) ? !repeatItem.value ? /* @__PURE__ */ _jsxC(BlockWrapper, {
          get Wrapper() {
            return Tag.value;
          },
          get block() {
            return processedBlock.value;
          },
          get context() {
            return props.context;
          },
          children: [
            /* @__PURE__ */ _jsxC(ComponentRef, {
              get componentRef() {
                return componentRefProps.value.componentRef;
              },
              get componentOptions() {
                return componentRefProps.value.componentOptions;
              },
              get blockChildren() {
                return componentRefProps.value.blockChildren;
              },
              get context() {
                return componentRefProps.value.context;
              },
              get registeredComponents() {
                return componentRefProps.value.registeredComponents;
              },
              get linkComponent() {
                return componentRefProps.value.linkComponent;
              },
              get builderBlock() {
                return componentRefProps.value.builderBlock;
              },
              get includeBlockProps() {
                return componentRefProps.value.includeBlockProps;
              },
              get isInteractive() {
                return componentRefProps.value.isInteractive;
              },
              [_IMMUTABLE]: {
                blockChildren: _fnSignal((p0) => p0.value.blockChildren, [
                  componentRefProps
                ], "p0.value.blockChildren"),
                builderBlock: _fnSignal((p0) => p0.value.builderBlock, [
                  componentRefProps
                ], "p0.value.builderBlock"),
                componentOptions: _fnSignal((p0) => p0.value.componentOptions, [
                  componentRefProps
                ], "p0.value.componentOptions"),
                componentRef: _fnSignal((p0) => p0.value.componentRef, [
                  componentRefProps
                ], "p0.value.componentRef"),
                context: _fnSignal((p0) => p0.value.context, [
                  componentRefProps
                ], "p0.value.context"),
                includeBlockProps: _fnSignal((p0) => p0.value.includeBlockProps, [
                  componentRefProps
                ], "p0.value.includeBlockProps"),
                isInteractive: _fnSignal((p0) => p0.value.isInteractive, [
                  componentRefProps
                ], "p0.value.isInteractive"),
                linkComponent: _fnSignal((p0) => p0.value.linkComponent, [
                  componentRefProps
                ], "p0.value.linkComponent"),
                registeredComponents: _fnSignal((p0) => p0.value.registeredComponents, [
                  componentRefProps
                ], "p0.value.registeredComponents")
              }
            }, 3, "jN_1"),
            (childrenWithoutParentComponent.value || []).map((child) => {
              return /* @__PURE__ */ _jsxC(Block, {
                block: child,
                get registeredComponents() {
                  return props.registeredComponents;
                },
                get linkComponent() {
                  return props.linkComponent;
                },
                get context() {
                  return props.context;
                },
                [_IMMUTABLE]: {
                  context: _fnSignal((p0) => p0.context, [
                    props
                  ], "p0.context"),
                  linkComponent: _fnSignal((p0) => p0.linkComponent, [
                    props
                  ], "p0.linkComponent"),
                  registeredComponents: _fnSignal((p0) => p0.registeredComponents, [
                    props
                  ], "p0.registeredComponents")
                }
              }, 3, child.id);
            })
          ],
          [_IMMUTABLE]: {
            Wrapper: _fnSignal((p0) => p0.value, [
              Tag
            ], "p0.value"),
            block: _fnSignal((p0) => p0.value, [
              processedBlock
            ], "p0.value"),
            context: _fnSignal((p0) => p0.context, [
              props
            ], "p0.context")
          }
        }, 1, "jN_2") : (repeatItem.value || []).map((data, index) => {
          return /* @__PURE__ */ _jsxC(RepeatedBlock, {
            get repeatContext() {
              return data.context;
            },
            get block() {
              return data.block;
            },
            get registeredComponents() {
              return props.registeredComponents;
            },
            get linkComponent() {
              return props.linkComponent;
            },
            [_IMMUTABLE]: {
              block: _wrapProp(data, "block"),
              linkComponent: _fnSignal((p0) => p0.linkComponent, [
                props
              ], "p0.linkComponent"),
              registeredComponents: _fnSignal((p0) => p0.registeredComponents, [
                props
              ], "p0.registeredComponents"),
              repeatContext: _wrapProp(data, "context")
            }
          }, 3, generateKey(index));
        }) : !repeatItem.value ? /* @__PURE__ */ _jsxC(ComponentRef, {
          get componentRef() {
            return componentRefProps.value.componentRef;
          },
          get componentOptions() {
            return componentRefProps.value.componentOptions;
          },
          get blockChildren() {
            return componentRefProps.value.blockChildren;
          },
          get context() {
            return componentRefProps.value.context;
          },
          get registeredComponents() {
            return componentRefProps.value.registeredComponents;
          },
          get linkComponent() {
            return componentRefProps.value.linkComponent;
          },
          get builderBlock() {
            return componentRefProps.value.builderBlock;
          },
          get includeBlockProps() {
            return componentRefProps.value.includeBlockProps;
          },
          get isInteractive() {
            return componentRefProps.value.isInteractive;
          },
          [_IMMUTABLE]: {
            blockChildren: _fnSignal((p0) => p0.value.blockChildren, [
              componentRefProps
            ], "p0.value.blockChildren"),
            builderBlock: _fnSignal((p0) => p0.value.builderBlock, [
              componentRefProps
            ], "p0.value.builderBlock"),
            componentOptions: _fnSignal((p0) => p0.value.componentOptions, [
              componentRefProps
            ], "p0.value.componentOptions"),
            componentRef: _fnSignal((p0) => p0.value.componentRef, [
              componentRefProps
            ], "p0.value.componentRef"),
            context: _fnSignal((p0) => p0.value.context, [
              componentRefProps
            ], "p0.value.context"),
            includeBlockProps: _fnSignal((p0) => p0.value.includeBlockProps, [
              componentRefProps
            ], "p0.value.includeBlockProps"),
            isInteractive: _fnSignal((p0) => p0.value.isInteractive, [
              componentRefProps
            ], "p0.value.isInteractive"),
            linkComponent: _fnSignal((p0) => p0.value.linkComponent, [
              componentRefProps
            ], "p0.value.linkComponent"),
            registeredComponents: _fnSignal((p0) => p0.value.registeredComponents, [
              componentRefProps
            ], "p0.value.registeredComponents")
          }
        }, 3, "jN_3") : (repeatItem.value || []).map((data, index) => {
          return /* @__PURE__ */ _jsxC(RepeatedBlock, {
            get repeatContext() {
              return data.context;
            },
            get block() {
              return data.block;
            },
            get registeredComponents() {
              return props.registeredComponents;
            },
            get linkComponent() {
              return props.linkComponent;
            },
            [_IMMUTABLE]: {
              block: _wrapProp(data, "block"),
              linkComponent: _fnSignal((p0) => p0.linkComponent, [
                props
              ], "p0.linkComponent"),
              registeredComponents: _fnSignal((p0) => p0.registeredComponents, [
                props
              ], "p0.registeredComponents"),
              repeatContext: _wrapProp(data, "context")
            }
          }, 3, generateKey(index));
        })
      ]
    }, 1, "jN_4") : null
  }, 1, "jN_5");
}, "Block_component_nnPv0RY0U0k"));
export {
  Block,
  Block as default
};
